import { BalanceSheetModel } from "@/models/balanceSheet.model";
import { AirLock, EquipmentType } from "@/models/equipment.model";
import { ElementaryZone, SiteModel } from "@/models/site.model";
import { VuexModule, Module, Mutation, Action } from "vuex-module-decorators";
import store from "..";
import {
  GET_ALL_ELEMENTARY_ZONES,
  GET_CLEAN_WASTES_ELEMENTARY_ZONES,
  GET_CLEAN_WORKERS_ELEMENTARY_ZONES,
  GET_EXTRACTORS_ELEMENTARY_ZONES,
  NB_ELEMENTARY_ZONE,
  RESET_SITE_STORE,
  UPDATE_AIRLOCK_IN_ZONE,
  UPDATE_BALANCE_SHEET,
  UPDATE_SITE_MODEL,
  UPDATE_ZONE_WITH_INDEX,
} from "../operators";

interface ZoneIndexPayload {
  zone: ElementaryZone;
  zoneIndex: number;
}

interface ZoneAirlockPayload {
  zone: ElementaryZone;
  equipmentType: EquipmentType;
  airlock: AirLock;
}

/**
 * This module contains the result of site stepper.
 * Airlocks are bound to a zone so we also save them in site store module.
 * In a zone the 2 fields personnelEquipment or wasteEquipment can both store 1 airlock.
 */
@Module
export default class SiteStore extends VuexModule {
  // site variables
  public site: SiteModel = {
    title: "",
    elementaryZones: [],
    totalVolume: 0,
  };

  public balanceSheet: BalanceSheetModel = new BalanceSheetModel();

  /**
   * Count all the elementary zones
   */
  public get [NB_ELEMENTARY_ZONE](): number {
    return this.site.elementaryZones.length;
  }

  /**
   * Get the complete list of elementary zones
   */
  public get [GET_ALL_ELEMENTARY_ZONES](): ElementaryZone[] {

    return this.site.elementaryZones;
  }

  /**
   * Get the list of elementary zones where we should add equipment related to workers decontamination
   */
  public get [GET_CLEAN_WORKERS_ELEMENTARY_ZONES](): ElementaryZone[] {
    return this.site.elementaryZones.filter((zone) => zone.personnelDecontamination);
  }

  /**
   * Get the list of elementary zones where we should add equipment related to wastes decontamination
   */
  public get [GET_CLEAN_WASTES_ELEMENTARY_ZONES](): ElementaryZone[] {
    return this.site.elementaryZones.filter((zone) => zone.wasteDecontamination);
  }

  /**
   * Get the list of elementary zones where we should add equipment related to extractors installation
   */
  public get [GET_EXTRACTORS_ELEMENTARY_ZONES](): ElementaryZone[] {
    return this.site.elementaryZones.filter((zone) => zone.extractorsInstallation);
  }

  @Mutation
  public [UPDATE_SITE_MODEL](site: SiteModel): void {
    this.site = site;
  }

  @Mutation
  public [UPDATE_BALANCE_SHEET](balanceSheet: BalanceSheetModel): void {
    this.balanceSheet = balanceSheet;
  }

  @Mutation
  public [UPDATE_ZONE_WITH_INDEX](payload: ZoneIndexPayload): void {
    this.site.elementaryZones[payload.zoneIndex] = payload.zone;
  }

  @Action
  public [RESET_SITE_STORE](): void {
    store.commit(
      UPDATE_SITE_MODEL,
      Object.assign({
        title: "",
        elementaryZones: [],
        totalVolume: 0,
      })
    );
    store.commit(UPDATE_BALANCE_SHEET, new BalanceSheetModel());
  }

  /**
   * Find the zone in payload and update his airlock.
   * @param payload
   */
  @Mutation
  public [UPDATE_AIRLOCK_IN_ZONE](payload: ZoneAirlockPayload): void {
    const correspondingZone = this.site.elementaryZones.find(
      (zone) => zone.name === payload.zone.name
    );
    if (correspondingZone) {
      if (payload.equipmentType === EquipmentType.workersAirLock) {
        correspondingZone.personnelEquipment = payload.airlock;
      } else if (payload.equipmentType === EquipmentType.wastesAirLock) {
        correspondingZone.wasteEquipment = payload.airlock;
      }
    }
  }
}
